import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CatalogFeature } from "@/types/catalog-feature";
import {
  PLAN_STATUSES,
  PlanFeatureValuePayload,
  PlanStatus,
  SubscriptionPlan,
  UpdateSubscriptionPlanPayload,
} from "@/types/subscription-plan";
import { useFeatures } from "../hooks/useFeatures";
import { useUpdateSubscriptionPlan } from "../hooks/useSubscriptionPlanMutations";
import { descriptionToFormString } from "../utils/planFormText";
import { PlanFeaturesBadgesField } from "./PlanFeaturesBadgesField";

interface EditDefaultPlanDialogProps {
  plan: SubscriptionPlan | null;
  onClose: () => void;
}

function buildFeaturePayload(
  ids: string[],
  featureOptions: CatalogFeature[],
  featureValueById: Readonly<Partial<Record<string, string>>>,
): PlanFeatureValuePayload[] {
  const featureById = new Map(
    featureOptions.map((feature) => [feature.id, feature]),
  );

  return ids.map((id) => {
    const feature = featureById.get(id);
    const rawValue =
      feature?.type === "NUMBER"
        ? (featureValueById[id] ?? String(feature.limit ?? "")).trim()
        : (featureValueById[id] ?? "true");

    return {
      featureId: id,
      value: rawValue,
    };
  });
}

export const EditDefaultPlanDialog: React.FC<EditDefaultPlanDialogProps> = ({
  plan,
  onClose,
}) => {
  const { t } = useTranslation();
  const open = plan !== null;
  const { data: features = [], isLoading: featuresLoading } = useFeatures(open);
  const { mutateAsync, isPending } = useUpdateSubscriptionPlan();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [status, setStatus] = useState<PlanStatus>("ACTIVE");
  const [isDefault, setIsDefault] = useState(true);
  const [enableCoreFeatures, setEnableCoreFeatures] = useState(true);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [featureValueById, setFeatureValueById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (plan) {
      setTitle(plan.title);
      setDescription(descriptionToFormString(plan.description));
      setPriceInput(String(plan.price));
      setCurrency(plan.currency);
      setStatus(
        PLAN_STATUSES.includes(plan.status as PlanStatus)
          ? (plan.status as PlanStatus)
          : "ACTIVE",
      );
      setIsDefault(plan.isDefault);
      setEnableCoreFeatures(plan.enableCoreFeatures === true);
      setSelectedFeatureIds(plan.features.map((f) => f.featureId));
      const nextFeatureValueById: Record<string, string> = {};
      for (const feature of plan.features) {
        if (typeof feature.value === "string") {
          nextFeatureValueById[feature.featureId] = feature.value;
        }
      }
      setFeatureValueById(nextFeatureValueById);
    }
  }, [plan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) {
      return;
    }

    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      toast.error(t("subscriptions.plans.default.edit.validation.title"));
      return;
    }

    const price = Number.parseFloat(priceInput.trim());
    if (!Number.isFinite(price) || price < 0) {
      toast.error(t("subscriptions.plans.default.edit.validation.price"));
      return;
    }

    const payload: UpdateSubscriptionPlanPayload = {
      title: trimmedTitle,
      description: description.trim(),
      price,
      currency: currency.trim() === "" ? "usd" : currency.trim().toLowerCase(),
      status,
      isDefault,
      enableCoreFeatures,
      features: buildFeaturePayload(
        selectedFeatureIds,
        featureOptions,
        featureValueById,
      ),
    };

    try {
      await mutateAsync({ id: plan.id, payload });
      toast.success(t("subscriptions.plans.default.edit.successToast"));
      onClose();
    } catch {
      toast.error(t("subscriptions.plans.default.edit.errorToast"));
    }
  };

  const featureOptions: CatalogFeature[] = features;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("subscriptions.plans.default.edit.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label={t("subscriptions.plans.default.edit.planTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
          <Textarea
            label={t("subscriptions.plans.default.edit.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              label={t("subscriptions.plans.default.edit.price")}
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
            <Input
              label={t("subscriptions.plans.default.edit.currency")}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.default.edit.status")}
            </span>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as PlanStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_STATUSES.map((planStatus) => (
                  <SelectItem key={planStatus} value={planStatus}>
                    {t(`subscriptions.plans.status.${planStatus}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.default.edit.isDefault")}
            </span>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>

          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.featureField.includeCore")}
            </span>
            <Switch
              checked={enableCoreFeatures}
              onCheckedChange={setEnableCoreFeatures}
            />
          </div>

          <PlanFeaturesBadgesField
            featureOptions={featureOptions}
            selectedFeatureIds={selectedFeatureIds}
            onSelectedFeatureIdsChange={setSelectedFeatureIds}
            featuresLoading={featuresLoading}
            featureValueById={featureValueById}
            onFeatureValueByIdChange={setFeatureValueById}
            labels={{
              section: t("subscriptions.plans.default.edit.features"),
              addPlaceholder: t(
                "subscriptions.plans.default.edit.selectFeature",
              ),
              removeFeature: t(
                "subscriptions.plans.default.edit.removeFeature",
              ),
              enabled: t("subscriptions.plans.featureField.enabled"),
              value: t("subscriptions.plans.featureField.value"),
            }}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {t("subscriptions.plans.default.edit.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("subscriptions.plans.default.edit.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
